import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { getSupabaseClient } from '../../services/supabase';

interface Keyword {
  id: number;
  keyword: string;
  is_primary: number;
  search_volume: number | null;
  estimated_visits: number | null;
}

interface KeywordManagementModalProps {
  open: boolean;
  onClose: () => void;
  urlId: string;
  urlPath: string;
  currentKeyword?: string;
  onKeywordsUpdated: () => void;
}

export const KeywordManagementModal: React.FC<KeywordManagementModalProps> = ({
  open,
  onClose,
  urlId,
  urlPath,
  currentKeyword,
  onKeywordsUpdated
}) => {
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddKeyword, setShowAddKeyword] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [addingKeyword, setAddingKeyword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (open && urlId) {
      fetchKeywords();
    }
  }, [open, urlId]);

  const fetchKeywords = async () => {
    setLoading(true);
    try {
      const supabase = await getSupabaseClient(true);
      
      const { data, error } = await supabase
        .from('keywords')
        .select('*')
        .eq('url_id', urlId)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      
      if (data) {
        // Cast to any to bypass TypeScript's strict checking
        const rawData = data as any[];
        
        // Map database fields to Keyword interface
        const mappedKeywords: Keyword[] = rawData.map(k => ({
          id: k.id,
          keyword: k.keyword,
          is_primary: k.is_primary,
          search_volume: k.search_volume || null,
          estimated_visits: k.estimated_visits || null
        }));
        
        // Sort by search volume manually since order by might not work
        mappedKeywords.sort((a, b) => {
          const aVolume = a.search_volume || 0;
          const bVolume = b.search_volume || 0;
          return bVolume - aVolume;
        });
        
        setKeywords(mappedKeywords);
        const primary = mappedKeywords.find(k => k.is_primary === 1);
        if (primary) {
          setSelectedPrimaryId(primary.id);
        }
      }
    } catch (error) {
      console.error('Error fetching keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPrimaryId) return;
    
    setSaving(true);
    try {
      const supabase = await getSupabaseClient(true);
      
      // First, set all keywords to non-primary
      await supabase
        .from('keywords')
        .update({ is_primary: 0 })
        .eq('url_id', urlId);
      
      // Then set the selected keyword as primary
      await supabase
        .from('keywords')
        .update({ is_primary: 1 })
        .eq('id', selectedPrimaryId);
      
      setSuccessMessage('✨ Primary keyword updated successfully!');
      setTimeout(() => {
        setSuccessMessage('');
        onKeywordsUpdated();
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error updating keywords:', error);
      alert('Error updating keywords. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;
    
    setAddingKeyword(true);
    try {
      const supabase = await getSupabaseClient(true);
      
      // Check if keyword already exists
      const { data: existing } = await supabase
        .from('keywords')
        .select('id')
        .eq('url_id', urlId)
        .eq('keyword', newKeyword.trim())
        .single();
      
      if (existing) {
        alert('This keyword already exists for this URL');
        return;
      }
      
      // Add the new keyword
      const { data, error } = await supabase
        .from('keywords')
        .insert([{
          url_id: urlId,
          keyword: newKeyword.trim(),
          is_primary: keywords.length === 0 ? 1 : 0
        }])
        .select('*')
        .single();
      
      if (error) throw error;
      
      if (data) {
        // Cast to any to bypass TypeScript's strict checking
        const rawData = data as any;
        
        // Map database fields to Keyword interface
        const mappedKeyword: Keyword = {
          id: rawData.id,
          keyword: rawData.keyword,
          is_primary: rawData.is_primary,
          search_volume: rawData.search_volume || null,
          estimated_visits: rawData.estimated_visits || null
        };
        setKeywords([...keywords, mappedKeyword]);
        if (keywords.length === 0) {
          setSelectedPrimaryId(rawData.id);
        }
        setNewKeyword('');
        setShowAddKeyword(false);
        setSuccessMessage('✨ Keyword added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error adding keyword:', error);
      alert('Error adding keyword. Please try again.');
    } finally {
      setAddingKeyword(false);
    }
  };

  const formatNumber = (num: number | null) => {
    if (!num) return '-';
    return num.toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Manage Keywords</Typography>
            <Typography variant="caption" color="text.secondary">
              {urlPath}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {successMessage && (
              <Alert 
                severity="success" 
                icon={<CheckCircleIcon />}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}
            
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Select which keyword should be the primary focus for this page
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddKeyword(true)}
                variant="outlined"
              >
                Add Keyword
              </Button>
            </Box>
            
            {keywords.length === 0 ? (
              <Alert severity="info">
                No keywords found for this URL. Add one to get started!
              </Alert>
            ) : (
              <RadioGroup
                value={selectedPrimaryId}
                onChange={(e) => setSelectedPrimaryId(parseInt(e.target.value))}
              >
                <List>
                  {keywords.map((keyword, index) => (
                    <React.Fragment key={keyword.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ px: 0 }}>
                        <FormControlLabel
                          value={keyword.id}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1">
                                  {keyword.keyword}
                                </Typography>
                                {keyword.is_primary === 1 && (
                                  <Chip 
                                    label="Current Primary" 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {keyword.keyword === currentKeyword && (
                                  <Chip 
                                    label="Viewing Now" 
                                    size="small" 
                                    color="secondary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Search Volume: {formatNumber(keyword.search_volume)}
                                </Typography>
                                {keyword.estimated_visits && (
                                  <Typography variant="caption" color="text.secondary">
                                    Est. Visits: {formatNumber(keyword.estimated_visits)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                          sx={{ width: '100%' }}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </RadioGroup>
            )}
            
            {showAddKeyword && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="New Keyword"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingKeyword) {
                      handleAddKeyword();
                    }
                  }}
                  disabled={addingKeyword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="e.g., statue of liberty tickets"
                  autoFocus
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleAddKeyword}
                    disabled={!newKeyword.trim() || addingKeyword}
                    startIcon={addingKeyword ? <CircularProgress size={16} /> : <AddIcon />}
                  >
                    {addingKeyword ? 'Adding...' : 'Add'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowAddKeyword(false);
                      setNewKeyword('');
                    }}
                    disabled={addingKeyword}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedPrimaryId || saving || keywords.length === 0}
          startIcon={saving ? <CircularProgress size={20} /> : <TrendingUpIcon />}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};